import {
  CategoryColor,
  DataLayer,
  DataLayerType,
  ParameterSelectDisplayType,
  ParameterType,
  Project,
} from '../api/api.ts';
import classNames from 'classnames';
import InputRange from '../common/ui/InputRange.tsx';
import Panel from '../common/Panel.tsx';
import NeumorphBox, { NeumorphType } from '../common/ui/NeumorphBox.tsx';
import {
  CoordinatePickerContext,
  DataLayerParameterValues,
  useCoordinatePickerContext,
} from './Map.tsx';
import {
  Fragment,
  ReactChildren,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  backgroundColorClassesForColor,
  hoverBackgroundColorClassesForColor,
  hoverTextColorClassesForColor,
  textColorClassesForColor,
} from '../utils/Colors.ts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faLocationCrosshairs,
} from '@fortawesome/free-solid-svg-icons';
import { LatLng } from 'leaflet';
import cn from '../utils/cn.ts';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.tsx';

const LabelWrapper = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}): ReactNode => {
  return (
    <>
      <div className={cn('mt-[12px]')}>{label}</div>
      <div>{children}</div>
    </>
  );
};

const StoryControls = ({
  index,
  count,
  updateIndex,
}: {
  index: number;
  count: number;
  updateIndex: (value: number) => void;
}): ReactNode => {
  return (
    <div>
      <span
        onClick={() => {
          updateIndex((index - 1) % count);
        }}
      >
        prev
      </span>{' '}
      <span>
        Marker {index + 1} / {count}
      </span>{' '}
      <span
        onClick={() => {
          updateIndex((index + 1) % count);
        }}
      ></span>
    </div>
  );
};

const Slider = ({
  value,
  min,
  max,
  step,
  labels,
  updateValue,
}: {
  value: number;
  min: number;
  max: number;
  step: number;
  labels?: string[];
  updateValue: (value: number) => void;
}) => {
  return (
    <InputRange
      value={value}
      onChange={updateValue}
      min={min}
      max={max}
      step={step}
      labels={labels}
      disabled={false}
    />
  );
};

const Selector = ({
  options,
  value,
  onUpdate,
}: {
  options: { value: string | number; label: string }[];
  value: any;
  onUpdate: (value: any) => void;
}) => {
  const valueDict = useMemo(
    () =>
      options.reduce(
        (carry, next) => {
          let stringified = JSON.stringify(next.value);
          while (carry[stringified] !== undefined) {
            stringified = stringified + '+';
          }
          carry[stringified] = { value: next.value, label: next.label };
          return carry;
        },
        {} as Record<string, { value: string | number; label: string }>
      ),
    [options]
  );
  const currentStringifiedValue = useMemo(() => {
    return Object.keys(valueDict).find(
      (stringifiedValue) => valueDict[stringifiedValue].value === value
    )!;
  }, [value, valueDict]);

  const onValueChange = (stringifiedValue: string) => {
    onUpdate(valueDict[stringifiedValue].value);
  };
  return (
    <div className={classNames('mb-[6px]')}>
      <Select value={currentStringifiedValue} onValueChange={onValueChange}>
        <SelectTrigger className="w-full">
          <SelectValue placeholder="- select -" />
        </SelectTrigger>
        <SelectContent>
          {Object.keys(valueDict).map((stringifiedValue) => (
            <SelectItem key={stringifiedValue} value={stringifiedValue}>
              {valueDict[stringifiedValue].label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

// // before shadcn
// const Selector = ({
//   options,
//   value,
//   onUpdate,
// }: {
//   options: { value: string | number; label: string }[];
//   value: any;
//   onUpdate: (value: any) => void;
// }) => {
//   const textColorClasses = ['highlight-color-text'];
//   const textHoverColorClasses = ['hover:highlight-color-text'];
//   const backgroundColorClasses = ['highlight-color-bg'];
//   const backgroundHoverColorClasses = ['hover:highlight-color-bg'];
//
//   return (
//     <div className={classNames('mb-[6px]')}>
//       <NeumorphBox
//         className={classNames('p-0!', 'flex', 'flex-col', 'overflow-hidden')}
//       >
//         {options.map((option, index, _options) => (
//           <button
//             key={index}
//             className={classNames(
//               'p-[6px]',
//               'cursor-pointer',
//               option.value === value
//                 ? [backgroundColorClasses, 'hover:bg-gray-400', 'text-gray-100']
//                 : [backgroundHoverColorClasses, 'hover:text-gray-100']
//             )}
//             onClick={() => {
//               if (value !== option.value) onUpdate(option.value);
//             }}
//           >
//             {option.label}
//           </button>
//         ))}
//       </NeumorphBox>
//     </div>
//   );
// };

const DependentChoice = ({
  value,
  subLabel,
  options,
  updateValue,
}: {
  value: [number | string, number | string];
  subLabel: string;
  options: {
    value: string | number;
    label: string;
    subOptions: { value: string | number; label: string }[];
  }[];
  updateValue: (value: [number | string, number | string]) => void;
}) => {
  const [values, setValues] = useState({
    selectValue: value[0],
    subSelectValue: value[1],
  });
  const subSelectOptions = useMemo((): {
    value: string | number;
    label: string;
  }[] => {
    const selectOption = options.find(
      (options) => options.value === values.selectValue
    );
    if (!selectOption)
      throw `No option found for value ${values.selectValue} in dependent choice selector`;
    return selectOption.subOptions;
  }, [values, options]);
  const updateSelectValue = (newValue: number | string) => {
    setValues((oldValues) => {
      if (oldValues.selectValue !== newValue) {
        const newOption = options.find((options) => options.value === newValue);
        if (!newOption)
          throw `No option found for value ${newValue} in dependent choice selector`;
        if (!newOption.subOptions.length)
          throw `No sub options found for option with value ${newValue} in dependent choice selector`;
        const newValues = {
          selectValue: newValue,
          subSelectValue: newOption.subOptions[0].value,
        };
        updateValue([newValues.selectValue, newValues.subSelectValue]);
        return newValues;
      } else {
        return oldValues;
      }
    });
  };
  const updateSubSelectValue = (newSubValue: number | string) => {
    setValues((oldValues) => {
      const newValues = {
        selectValue: oldValues.selectValue,
        subSelectValue: newSubValue,
      };
      updateValue([newValues.selectValue, newValues.subSelectValue]);
      return newValues;
    });
  };
  return (
    <>
      <Selector
        options={options}
        value={values.selectValue}
        onUpdate={updateSelectValue}
      />
      <LabelWrapper label={subLabel}>
        <Selector
          options={subSelectOptions}
          value={values.subSelectValue}
          onUpdate={updateSubSelectValue}
        />
      </LabelWrapper>
    </>
  );
};

const CoordinatePicker = ({
  value,
  onUpdateValue,
}: {
  value: LatLng;
  onUpdateValue: (latlng: LatLng) => void;
}) => {
  const { requestCoordinate, currentRequest, resetRequest } =
    useCoordinatePickerContext();
  useEffect(() => {
    const reset = resetRequest;
    return () => {
      reset && reset();
    };
  }, []);
  const click = useCallback(() => {
    if (!resetRequest || !requestCoordinate)
      throw `CoordinatePickerContext not initialized`;
    if (currentRequest) {
      resetRequest();
    } else {
      requestCoordinate(onUpdateValue);
    }
  }, [onUpdateValue, requestCoordinate, resetRequest]);
  return (
    <div className={classNames('my-[6px]', 'flex', 'items-center')}>
      <div className={cn('flex-0')}>
        <NeumorphBox
          className={classNames(
            'w-[30px]',
            'h-[30px]',
            'flex',
            'flex-col',
            'justify-center',
            'p-[6px]!',
            'ml-0',
            currentRequest ? 'text-cliwac-orange' : undefined
          )}
          type={NeumorphType.Hoverable}
          onClick={click}
        >
          <FontAwesomeIcon icon={faLocationCrosshairs} />
        </NeumorphBox>
      </div>
      <div className={cn('flex', 'flex-col', 'justify-between')}>
        <div className={cn('flex-1')}>
          <span className={cn('inline-block', 'w-[2.7em]')}>Lat:</span>&nbsp;
          {value.lat.toFixed(5)}
        </div>
        <div className={cn('flex-1')}>
          <span className={cn('inline-block', 'w-[2.7em]')}>Long:</span>&nbsp;
          {value.lng.toFixed(5)}
        </div>
      </div>
    </div>
  );
};

export default function Controls({
  layer,
  parameterValues,
  updateLayerParams,
}: {
  layer: DataLayer;
  parameterValues: DataLayerParameterValues;
  updateLayerParams: (settings: DataLayerParameterValues) => void;
}): ReactNode {
  switch (layer.type) {
    case DataLayerType.Story:
      return (
        <StoryControls
          index={parameterValues['index'] as number}
          count={layer.layers.length}
          updateIndex={(value: number) => {
            updateLayerParams({ index: value });
          }}
        />
      );
    case DataLayerType.Data:
      return (
        <>
          {Object.keys(layer.parameters).map((paramKey) => {
            const parameter = layer.parameters[paramKey];
            switch (parameter.type) {
              case ParameterType.Coordinate:
                const coordinateValue = parameterValues[paramKey] as [
                  number,
                  number,
                ];
                return (
                  <LabelWrapper
                    key={layer.id + '-' + paramKey}
                    label={parameter.label}
                  >
                    <CoordinatePicker
                      value={new LatLng(coordinateValue[0], coordinateValue[1])}
                      onUpdateValue={(latlng) => {
                        updateLayerParams({
                          [paramKey]: [latlng.lat, latlng.lng],
                        });
                      }}
                    />
                  </LabelWrapper>
                );
              case ParameterType.Slider:
                const sliderValue = parameterValues[paramKey] as number;
                return (
                  <LabelWrapper
                    key={layer.id + '-' + paramKey}
                    label={parameter.label}
                  >
                    <Slider
                      value={sliderValue}
                      min={parameter.min}
                      max={parameter.max}
                      step={parameter.step}
                      updateValue={(value) => {
                        updateLayerParams({ [paramKey]: value });
                      }}
                    />
                  </LabelWrapper>
                );
              case ParameterType.DependentChoice:
                const depChoiceValue = parameterValues[paramKey] as [
                  number | string,
                  number | string,
                ];
                return (
                  <LabelWrapper
                    key={layer.id + '-' + paramKey}
                    label={parameter.label}
                  >
                    <DependentChoice
                      subLabel={parameter.subLabel}
                      value={depChoiceValue}
                      options={parameter.options}
                      updateValue={(value) => {
                        console.log('whattiup');
                        updateLayerParams({ [paramKey]: value });
                      }}
                    />
                  </LabelWrapper>
                );
              case ParameterType.Select:
                const options = parameter.options;
                const selectValue = parameterValues[paramKey] as
                  | string
                  | number;
                switch (parameter.displayAs) {
                  case ParameterSelectDisplayType.Choice:
                    return (
                      <LabelWrapper
                        key={layer.id + '-' + paramKey}
                        label={parameter.label}
                      >
                        <Selector
                          options={options}
                          value={selectValue}
                          onUpdate={(updateValue) => {
                            updateLayerParams({ [paramKey]: updateValue });
                          }}
                        />
                      </LabelWrapper>
                    );
                  case ParameterSelectDisplayType.Dropdown:
                    // todo dropdown
                    return (
                      <LabelWrapper
                        key={layer.id + '-' + paramKey}
                        label={parameter.label}
                      >
                        <Selector
                          options={parameter.options}
                          value={selectValue}
                          onUpdate={(updateValue) => {
                            updateLayerParams({ [paramKey]: updateValue });
                          }}
                        />
                      </LabelWrapper>
                    );
                  case ParameterSelectDisplayType.Slider:
                    const parameterValue = parameterValues[paramKey] as number;
                    const sliderValue = parameter.options.findIndex(
                      (opt) => opt.value === parameterValue
                    );
                    if (sliderValue === -1)
                      throw `Could not find index for value ${parameterValue}`;
                    const labels = parameter.options.map(
                      (option) => option.label
                    );
                    return (
                      <LabelWrapper
                        key={layer.id + '-' + paramKey}
                        label={parameter.label}
                      >
                        <Slider
                          value={sliderValue}
                          min={0}
                          max={parameter.options.length - 1}
                          step={1}
                          labels={labels}
                          updateValue={(value) => {
                            updateLayerParams({
                              [paramKey]: options[value].value,
                            });
                          }}
                        />
                      </LabelWrapper>
                    );
                }
            }
          })}
        </>
      );
  }
  // return (
  //   <Panel className={classNames('relative', 'overflow-auto', 'h-full')}>
  //     <h3 className={classNames()}>
  //       {project.shortName}&nbsp;|&nbsp;
  //       <span className={classNames('text-[0.7em]')}>{layer.shortName}</span>
  //     </h3>
  //     {layer.p.type === DataLayerSettingsType.Discrete && (
  //       <>
  //         <div className={classNames('mb-[4px]')}>
  //           {layer.type === DataLayerType.GeoTiffMultiSingleDiscrete &&
  //             layer.selectionLabel}
  //         </div>
  //         <div className={classNames('mb-[6px]')}>
  //           {layer.type === DataLayerType.GeoTiffMultiSingleDiscrete && (
  //             <NeumorphBox
  //               className={classNames(
  //                 'p-0!',
  //                 'flex',
  //                 'flex-col',
  //                 'overflow-hidden'
  //               )}
  //             >
  //               {layer.layers.map((subLayer, index, _subLayers) => (
  //                 <button
  //                   key={index}
  //                   className={classNames(
  //                     index ===
  //                       (parameterValues as DataLayerSettingsDiscrete).index
  //                       ? classNames(projectColorClasses, 'text-gray-100')
  //                       : undefined,
  //                     projectColorHoverClasses,
  //                     'hover:text-gray-100',
  //                     'p-[6px]'
  //                   )}
  //                   onClick={() => {
  //                     updateParams({
  //                       type: DataLayerSettingsType.Discrete,
  //                       index: index,
  //                     });
  //                   }}
  //                 >
  //                   {subLayer.label}
  //                 </button>
  //               ))}
  //             </NeumorphBox>
  //           )}
  //         </div>
  //       </>
  //     )}
  //     {layer.defaultSettings.type === DataLayerSettingsType.Series && (
  //       <>
  //         <div className={classNames('mb-[4px]')}>
  //           {layer.type === DataLayerType.GeoTiffMultiSingleSeries &&
  //             layer.selectionLabel}
  //         </div>
  //         <div className={classNames('mb-[6px]')}>
  //           {layer.type === DataLayerType.GeoTiffMultiSingleSeries && (
  //             <div className={classNames('max-w-[350px]')}>
  //               <InputRange
  //                 value={(parameterValues as DataLayerSettingsSeries).index}
  //                 onChange={(newIndex: number) => {
  //                   updateParams({
  //                     type: DataLayerSettingsType.Series,
  //                     index: newIndex,
  //                   });
  //                 }}
  //                 min={0}
  //                 max={layer.layers.length - 1}
  //                 labels={(
  //                   layer as DataLayerGeoTiffMultiSingleSeries
  //                 ).layers.map((l) => l.label)}
  //                 step={1}
  //                 id={'input-' + layer.id}
  //                 disabled={false}
  //               />
  //             </div>
  //           )}
  //         </div>
  //       </>
  //     )}
  //     {layer.type === DataLayerType.GeoJson &&
  //       layer.propertySelector.type !== GeoJsonPropertySelectorType.None && (
  //         <>
  //           <div className={classNames('mb-[4px]')}>
  //             {layer.propertySelector.selectorLabel}:
  //           </div>
  //           {layer.propertySelector.type ===
  //             GeoJsonPropertySelectorType.Switch && (
  //             <div className={classNames('mb-[6px]')}>
  //               <NeumorphBox
  //                 className={classNames(
  //                   'p-0!',
  //                   'flex',
  //                   'flex-col',
  //                   'overflow-hidden'
  //                 )}
  //               >
  //                 {layer.propertySelector.values.map(
  //                   (valueAndLabel, index, _valueAndLabels) => (
  //                     <button
  //                       key={index}
  //                       className={classNames(
  //                         index ===
  //                           (parameterValues as DataLayerSettingsDiscrete).index
  //                           ? classNames(projectColorClasses, 'text-gray-100')
  //                           : undefined,
  //                         projectColorHoverClasses,
  //                         'hover:text-gray-100',
  //                         'p-[6px]'
  //                       )}
  //                       onClick={() => {
  //                         updateParams({
  //                           type: DataLayerSettingsType.Discrete,
  //                           index: index,
  //                         });
  //                       }}
  //                     >
  //                       {valueAndLabel.label}
  //                     </button>
  //                   )
  //                 )}
  //               </NeumorphBox>
  //             </div>
  //           )}
  //           {layer.propertySelector.type ===
  //             GeoJsonPropertySelectorType.Slider && (
  //             <>
  //               <InputRange
  //                 value={
  //                   (parameterValues as DataLayerSettingsGeoJsonProperty).index
  //                 }
  //                 onChange={(newIndex: number) => {
  //                   updateParams({
  //                     type: DataLayerSettingsType.GeoJsonProperty,
  //                     index: newIndex,
  //                   });
  //                 }}
  //                 min={0}
  //                 max={layer.propertySelector.values.length - 1}
  //                 labels={layer.propertySelector.values.map((v) => v.label)}
  //                 step={1}
  //                 id={'input-' + layer.id}
  //                 disabled={false}
  //               />
  //             </>
  //           )}
  //         </>
  //       )}
  //   </Panel>
  // );
}
